import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { operations, quoteFinancial, quoteTimeline } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDate,
  formatDateTime,
  formatDocument,
  formatMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Stepper } from '@/shared/ui/Stepper';
import { Textarea } from '@/shared/ui/Textarea';
import { Timeline } from '@/shared/ui/Timeline';
import { useToast } from '@/shared/ui/useToast';

import { itemDaLinhaDoTempo, origensDoFollowUp } from './acompanhamento';
import { nomeDoResumo, salvarArquivoDoFinanceiro } from './financeiroDaCotacao';
import { QuoteDeclineDialog } from './QuoteDeclineDialog';

/**
 * Linha do tempo da cotacao (`/propostas/timeline/:id` do OnPoint; COT-101 a COT-110,
 * P-COT-3).
 *
 * Pagina propria, como no OnPoint (decisao 6 da 11.3 do plano 49, pelo comportamento
 * do OnPoint): as fases Cotacao, Proposta e Emissao no topo; "Dados da cotacao" com a
 * Importancia Segurada e o Premio que a operacao ja tem, sem calculo; o "Historico da
 * movimentacao", do mais recente para o mais antigo, com a situacao, o status
 * (Aprovado, Recusado) e o lado de quem executou (Seguradora, Corretor); os documentos
 * pedidos ao corretor e as informacoes datadas; e os botoes "Cancelar cotacao" e
 * "Baixar resumo".
 *
 * "Baixar resumo" baixa o resumo da cotacao do pacote financeiro (COT-110, P-COT-4), o
 * mesmo do quadro Financeiro da cotacao.
 */
export function OperationTimelinePage() {
  const { operationId = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cancelando, setCancelando] = useState(false);
  const [texto, setTexto] = useState('');
  const [grupo, setGrupo] = useState('');

  const operacao = useQuery({
    queryKey: ['operations', operationId],
    queryFn: ({ signal }) => operations.get(operationId, signal),
    enabled: operationId !== '',
  });
  const linha = useQuery({
    queryKey: ['quote-timeline', operationId, 'timeline'],
    queryFn: ({ signal }) => quoteTimeline.timeline(operationId, signal),
    enabled: operationId !== '',
  });
  const info = useQuery({
    queryKey: ['quote-timeline', operationId, 'info'],
    queryFn: ({ signal }) => quoteTimeline.info(operationId, signal),
    enabled: operationId !== '',
  });

  const registrar = useMutation({
    mutationFn: () =>
      quoteTimeline.addInfo(operationId, {
        text: texto.trim(),
        ...(grupo ? { situation_group: grupo } : {}),
      }),
    onSuccess: () => {
      toast.success('Informacao registrada na linha do tempo.');
      setTexto('');
      setGrupo('');
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
      void queryClient.invalidateQueries({ queryKey: ['operations', operationId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const baixarResumo = useMutation({
    mutationFn: () => quoteFinancial.summary(operationId),
    onSuccess: (arquivo) => {
      if (operacao.data) {
        salvarArquivoDoFinanceiro(arquivo, nomeDoResumo(operacao.data, arquivo));
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submeter(event: FormEvent) {
    event.preventDefault();
    registrar.mutate();
  }

  if (operacao.isPending || linha.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (operacao.isError || linha.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Linha do tempo da cotacao" />
        <Alert tone="danger" title="Nao foi possivel abrir a linha do tempo">
          {(operacao.error ?? linha.error)?.message}
        </Alert>
      </div>
    );
  }

  const op = operacao.data;
  const tempo = linha.data;
  const podeCancelar =
    !op.is_terminal && op.transitions.some((item) => item.stage === 'declinada');
  const interrompida = tempo.phases.some((fase) => fase.state === 'interrompida');
  const todasConcluidas = tempo.phases.every((fase) => fase.state === 'concluida');
  const grupoDaModalidade = op.modality_info?.group?.label;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={`Linha do tempo da cotacao ${op.proposal_number ?? op.number}`}
        description={op.subject}
        meta={
          <>
            <StatusBadge kind="faseCotacao" value={tempo.phase} />
            {op.proposal_status ? (
              <StatusBadge kind="cotacao" value={op.proposal_status} />
            ) : null}
          </>
        }
        actions={
          <>
            <Link
              to={`/operacoes/${op.id}`}
              className="inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100"
            >
              Voltar a cotacao
            </Link>
            <Button
              variant="secondary"
              loading={baixarResumo.isPending}
              onClick={() => baixarResumo.mutate()}
            >
              Baixar resumo
            </Button>
            {podeCancelar ? (
              <Button variant="danger" onClick={() => setCancelando(true)}>
                Cancelar cotacao
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <CardBody>
          <Stepper
            label="Fases da cotacao"
            steps={tempo.phases.map((fase) => ({
              key: fase.key,
              label: fase.label,
              state: fase.state === 'concluida' ? 'concluida' : undefined,
            }))}
            currentKey={todasConcluidas ? '' : tempo.phase}
            terminated={interrompida}
          />
        </CardBody>
      </Card>

      {op.stage === 'declinada' ? (
        <Alert
          tone="danger"
          title={
            op.proposal_status === 'cancelada'
              ? 'Cotacao cancelada'
              : 'Cotacao recusada'
          }
        >
          {op.decline_reason}
        </Alert>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <Card>
            <CardHeader
              title="Historico da movimentacao"
              description="Do mais recente para o mais antigo. Legenda: Aprovado, Recusado, Seguradora, Corretor."
              action={
                tempo.current ? (
                  <StatusBadge
                    kind="statusSituacao"
                    value={tempo.current.situation_status}
                  />
                ) : null
              }
            />
            <Timeline items={tempo.items.map(itemDaLinhaDoTempo)} />
          </Card>
        </div>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader title="Dados da cotacao" />
            <CardBody>
              <dl className="grid gap-3 text-sm">
                <div>
                  <dt className="text-2xs text-ink-500">Importancia Segurada</dt>
                  <dd className="font-medium text-ink-900">
                    {formatMoney(op.insured_amount)}
                  </dd>
                </div>
                <div>
                  <dt className="text-2xs text-ink-500">Premio</dt>
                  <dd className="font-medium text-ink-900">
                    {formatMoney(op.premium)}
                  </dd>
                </div>
                <div>
                  <dt className="text-2xs text-ink-500">Tomador</dt>
                  <dd className="text-ink-800">
                    {op.policyholder.label}
                    {op.policyholder.document
                      ? ` - ${formatDocument(op.policyholder.document)}`
                      : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-2xs text-ink-500">Segurado</dt>
                  <dd className="text-ink-800">{op.insured?.label ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-2xs text-ink-500">Grupo/Modalidade</dt>
                  <dd className="text-ink-800">
                    {grupoDaModalidade ? `${grupoDaModalidade} / ` : ''}
                    {op.modality.label}
                  </dd>
                </div>
                {tempo.current ? (
                  <div>
                    <dt className="text-2xs text-ink-500">Situacao atual</dt>
                    <dd className="flex flex-wrap items-center gap-1.5 text-ink-800">
                      {tempo.current.situation}
                      <StatusBadge
                        kind="ladoSituacao"
                        value={tempo.current.actor_side}
                      />
                    </dd>
                  </div>
                ) : null}
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Documentos solicitados"
              description="Pedidos ao corretor nesta cotacao."
              action={
                info.data?.has_pending_documents ? (
                  <Badge tone="warning">Ha documento pendente</Badge>
                ) : null
              }
            />
            {info.isError ? (
              <CardBody>
                <Alert tone="danger" title="Nao foi possivel carregar">
                  {info.error.message}
                </Alert>
              </CardBody>
            ) : (info.data?.requested_documents ?? []).length === 0 ? (
              <EmptyState icon="file" title="Nenhum documento solicitado" />
            ) : (
              <ul className="divide-y divide-line">
                {(info.data?.requested_documents ?? []).map((documento) => (
                  <li key={documento.id} className="px-5 py-3">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink-800">
                      {documento.title}
                      <Badge
                        tone={documento.status === 'recebido' ? 'success' : 'warning'}
                      >
                        {documento.status === 'recebido'
                          ? 'Recebido'
                          : documento.status === 'dispensado'
                            ? 'Dispensado'
                            : 'Pendente'}
                      </Badge>
                    </p>
                    <p className="text-2xs text-ink-500">
                      Pedido em {formatDate(documento.requested_at)}
                      {documento.requested_label
                        ? ` por ${documento.requested_label}`
                        : ''}
                    </p>
                    {documento.request_note ? (
                      <p className="mt-0.5 text-2xs text-ink-600">
                        {documento.request_note}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Informacoes"
              description="Registros datados da cotacao. Nao mudam a situacao nem a fase."
            />
            {(info.data?.entries ?? []).length === 0 ? (
              <CardBody>
                <p className="text-sm text-ink-500">Nenhuma informacao registrada.</p>
              </CardBody>
            ) : (
              <ul className="divide-y divide-line">
                {(info.data?.entries ?? []).map((entrada) => (
                  <li key={entrada.id} className="px-5 py-3">
                    <p className="text-sm text-ink-800">{entrada.text}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-2xs text-ink-500">
                      <StatusBadge
                        kind="grupoSituacao"
                        value={entrada.situation_group}
                      />
                      {entrada.actor_label} - {formatDateTime(entrada.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {can('operacoes.operacao.update') ? (
              <CardBody className="border-t border-line">
                <form onSubmit={submeter} className="flex flex-col gap-3">
                  <Textarea
                    label="Informacao da linha do tempo"
                    value={texto}
                    rows={2}
                    maxLength={2000}
                    onChange={(event) => setTexto(event.target.value)}
                  />
                  <Select
                    label="Grupo da informacao"
                    options={origensDoFollowUp
                      .filter((item) => item.value !== 'nomeacao')
                      .map((item) =>
                        item.value === ''
                          ? { value: '', label: 'Pela etapa atual' }
                          : item,
                      )}
                    value={grupo}
                    onChange={(event) => setGrupo(event.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      size="sm"
                      loading={registrar.isPending}
                      disabled={texto.trim() === ''}
                    >
                      Registrar informacao
                    </Button>
                  </div>
                </form>
              </CardBody>
            ) : null}
          </Card>
        </div>
      </div>

      <QuoteDeclineDialog
        operationId={op.id}
        mode="cancelamento"
        open={cancelando}
        onClose={() => setCancelando(false)}
      />
    </div>
  );
}
